/**
 * GeoLink API Client
 * Integrates with Stellar GeoLink backend APIs for location services,
 * nearby users, NFTs, smart contracts, and session management
 */

// In the browser all GeoLink calls MUST go through the GeoTrust backend proxy
// to avoid CORS rejections (testnet.stellargeolink.com doesn't allow cross-origin
// requests from geotrust.stellargeolink.com).  The backend's /proxy/* catch-all
// route forwards every request to the real GeoLink URL server-side, injecting
// the server-side API key — so the browser never holds a GeoLink credential.
const GEOLINK_API_BASE =
  typeof window !== 'undefined'
    ? '/api/v2/geolink/proxy'   // browser: route through our own backend
    : (process.env.REACT_APP_GEOLINK_API_URL || 'https://testnet.stellargeolink.com'); // SSR/node: direct

export interface LocationUpdate {
  public_key: string;
  blockchain: string;
  latitude: number;
  longitude: number;
  wallet_type_id?: number;
  description?: string;
}

export interface NearbyUser {
  public_key: string;
  latitude: number;
  longitude: number;
  distance: number;
  wallet_type?: string;
  description?: string;
  last_updated?: string;
}

export interface NearbyNFT {
  id: number;
  name: string;
  description?: string;
  latitude: number;
  longitude: number;
  distance: number;
  contract_address?: string;
  token_id?: string;
  image_url?: string;
  server_url?: string;
  ipfs_hash?: string;
  collection_name?: string;
}

export interface NearbyContract {
  id: number;
  type?: string;                        // "contract_rule"
  rule_name?: string;
  rule_type?: 'location' | 'proximity' | 'geofence';
  contract_name: string;
  contract_address: string;
  function_name?: string;
  // Pin position — always present. For geofences this is the centroid.
  latitude: number;
  longitude: number;
  // Circle centre — present for location/proximity, null for geofence
  center_latitude?: number | null;
  center_longitude?: number | null;
  radius_meters?: number | null;
  // Polygon boundary — present for geofence rules, null otherwise
  boundary_geojson?: { type: 'Polygon' | 'MultiPolygon'; coordinates: any[] } | null;
  geofence_id?: number | null;
  // Trigger / execution
  trigger_on?: 'enter' | 'exit' | 'dwell';
  min_location_duration_seconds?: number | null;
  auto_execute?: boolean;
  requires_confirmation?: boolean;
  requires_webauthn?: boolean;
  use_smart_wallet?: boolean;
  // Rate limiting
  max_executions_per_public_key?: number | null;
  execution_time_window_seconds?: number | null;
  // Wallet targeting
  target_wallet_public_key?: string | null;
  // Status
  is_active?: boolean;
  distance?: number;                    // metres from search centre
  network?: 'testnet' | 'mainnet';
  function_mappings?: Record<string, any>;
  discovered_functions?: any[];
  description?: string;
  // Legacy fields for backward compatibility
  name?: string;
  functions?: any[];
}

export interface SessionData {
  session_id: string;
  player1: string;
  player2?: string;
  state: 'waiting' | 'active' | 'ended';
  created_at: string;
  updated_at: string;
  location?: {
    latitude: number;
    longitude: number;
  };
}

class GeoLinkApiClient {
  private walletProviderKey: string;
  private dataConsumerKey: string;
  private baseUrl: string;
  /**
   * Wallet currently driving the session — sent as `X-Public-Key` on every
   * GeoLink request so the server can resolve the caller per the multi-tenant
   * scoping integration guide §3.2 (priority: JWT → header → body).
   *
   * Callers should keep this in sync with the connected wallet via
   * `setConnectedPublicKey()`. When unset (passive Wallet-Provider /
   * Data-Consumer flows) we fall back to API-key-only behaviour, which
   * GeoLink leaves unscoped for back-compat.
   */
  private connectedPublicKey: string | null = null;

  /**
   * Keys default to empty: browser callers rely on the backend proxy to inject
   * the server-side credential. Only Node/test callers talking to GeoLink
   * directly pass keys here.
   */
  constructor(walletProviderKey?: string, dataConsumerKey?: string, baseUrl?: string) {
    this.walletProviderKey = walletProviderKey || '';
    this.dataConsumerKey = dataConsumerKey || '';
    this.baseUrl = baseUrl || GEOLINK_API_BASE;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    useWalletProviderKey: boolean = false,
    suppressErrorLogging: boolean = false
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const apiKey = useWalletProviderKey ? this.walletProviderKey : this.dataConsumerKey;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    // Only Node/test callers that were constructed with an explicit key send
    // one; the proxy injects it for everyone else and discards what we send.
    if (apiKey && apiKey.trim().length > 0) {
      headers['X-API-Key'] = apiKey.trim();
    }
    // Multi-tenant identifier per docs/geolink-multi-tenant-scoping-response.md §3.2.
    // GeoLink resolves the caller via JWT → X-Public-Key → body; we always
    // send the header when a wallet is connected so non-admin sessions are
    // scoped correctly without us having to mutate every URL or body.
    if (this.connectedPublicKey) {
      headers['X-Public-Key'] = this.connectedPublicKey;
    }

    // Merge any additional headers from options (but don't override the
    // ones GeoLink relies on for auth / scoping).
    if (options.headers) {
      const additionalHeaders = options.headers as Record<string, string>;
      Object.keys(additionalHeaders).forEach(key => {
        const lowerKey = key.toLowerCase();
        if (lowerKey !== 'x-api-key' && lowerKey !== 'content-type' && lowerKey !== 'x-public-key') {
          headers[key] = additionalHeaders[key];
        }
      });
    }

    // Debug logging (similar to xyz-wallet) - skip for endpoints that may not be available
    if (!suppressErrorLogging) {
      // console.log(`[GeoLinkAPI] Request to ${endpoint}:`, {
      //   url,
      //   method: options.method || 'GET',
      //   hasApiKey: true,
      //   apiKeyLength: apiKey.trim().length,
      //   apiKeyPreview: `${apiKey.trim().substring(0, 8)}...${apiKey.trim().substring(apiKey.trim().length - 4)}`,
      //   useWalletProviderKey,
      //   headersKeys: Object.keys(headers),
      // });
    }

    try {
      // Create fetch options - ensure headers are set correctly
      const fetchOptions: RequestInit = {
        ...options,
        headers: headers, // Explicitly set headers (don't merge with options.headers)
      };

      const response = await fetch(url, fetchOptions);

      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
        let errorDetails: any = null;
        try {
          errorDetails = await response.json();
          errorMessage = errorDetails.error || errorDetails.message || errorMessage;
        } catch {
          // If JSON parsing fails, try to get text
          try {
            const text = await response.text();
            if (text) errorMessage = text;
          } catch {
            // If text parsing also fails, use status text
          }
        }
        
        // Log detailed error information (unless suppressed for expected failures)
        if (!suppressErrorLogging) {
          // console.error(`[GeoLinkAPI] Request failed for ${endpoint}:`, {
          //   status: response.status,
          //   statusText: response.statusText,
          //   errorMessage,
          //   errorDetails,
          //   url,
          //   method: options.method || 'GET',
          //   apiKeyLength: apiKey.trim().length,
          //   apiKeyPreview: `${apiKey.trim().substring(0, 4)}...${apiKey.trim().substring(apiKey.trim().length - 4)}`,
          //   useWalletProviderKey,
          // });
        }
        
        // Special handling for 401 (Unauthorized)
        if (response.status === 401) {
          errorMessage = `API key required or invalid. Check that REACT_APP_GEOLINK_${useWalletProviderKey ? 'WALLET_PROVIDER' : 'DATA_CONSUMER'}_KEY is set correctly.`;
        }
        
        // Special handling for 404 (Not Found)
        if (response.status === 404) {
          errorMessage = `Endpoint not found: ${endpoint}`;
        }
        
        // Special handling for 400 (Bad Request) - may indicate missing parameters
        if (response.status === 400) {
          errorMessage = errorDetails?.error || errorDetails?.message || 'Bad Request - Missing required parameters';
        }
        
        // Create error with status code for caller to handle
        const apiError = new Error(errorMessage);
        (apiError as any).status = response.status;
        throw apiError;
      }

      return await response.json();
    } catch (error: any) {
      // Only log errors that aren't 400/404 (which are expected for some endpoints)
      // Check both error.status and error.message to catch all cases
      const isExpectedError = error.status === 400 || error.status === 404 ||
        error.message?.includes('400') || error.message?.includes('404') ||
        error.message?.includes('not found') || error.message?.includes('Missing required parameters');
      
      if (!isExpectedError) {
        // console.error(`[GeoLinkAPI] Request failed for ${endpoint}:`, error);
      }
      throw error;
    }
  }

  /**
   * Update wallet location (uses Wallet Provider key)
   */
  async updateLocation(data: LocationUpdate): Promise<any> {
    // console.log('[GeoLinkAPI] Updating location:', {
    //   public_key: data.public_key.substring(0, 8) + '...',
    //   latitude: data.latitude,
    //   longitude: data.longitude,
    // });
    
    // Match xyz-wallet format: use 'Stellar' (capital S) for blockchain
    const requestBody = {
      ...data,
      blockchain: 'Stellar', // xyz-wallet uses capital S
    };
    
    return this.request('/api/location/update', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    }, true); // Use wallet provider key
  }

  /**
   * Get nearby wallets/users via GET /api/location/nearby
   * Uses Data Consumer key. Radius is passed as-is; the server may cap it internally.
   */
  async getNearbyUsers(
    latitude: number,
    longitude: number,
    radius: number = 100000,
    _publicKey?: string
  ): Promise<NearbyUser[]> {
    try {
      const result = await this.request<Array<{
        public_key: string;
        latitude: number | string;
        longitude: number | string;
        blockchain?: string;
        last_updated?: string;
        provider_name?: string;
      }>>(
        `/api/location/nearby?lat=${latitude}&lon=${longitude}&radius=${Math.round(radius)}`,
        {},
        false // Data Consumer key
      );
      if (!Array.isArray(result)) return [];
      return result
        .filter(w => w.public_key && w.latitude != null && w.longitude != null)
        .map(w => ({
          public_key: w.public_key,
          latitude: typeof w.latitude === 'number' ? w.latitude : parseFloat(String(w.latitude)),
          longitude: typeof w.longitude === 'number' ? w.longitude : parseFloat(String(w.longitude)),
          distance: 0,
          last_updated: w.last_updated,
        }))
        .filter(w => !isNaN(w.latitude) && !isNaN(w.longitude));
    } catch {
      return [];
    }
  }

  /**
   * Get nearby NFTs (uses Data Consumer key)
   */
  async getNearbyNFTs(
    latitude: number,
    longitude: number,
    radius: number = 10000
  ): Promise<NearbyNFT[]> {
    // console.log('[GeoLinkAPI] Fetching nearby NFTs:', { latitude, longitude, radius });
    const response = await this.request<{ nfts?: NearbyNFT[] }>(
      `/api/nft/nearby?latitude=${latitude}&longitude=${longitude}&radius=${radius}`,
      {},
      false // Use data consumer key
    );
    return response.nfts || [];
  }

  /**
   * Get nearby smart contract markers (uses Data Consumer key)
   */
  async getNearbyContracts(
    latitude: number,
    longitude: number,
    radius: number = 10000
  ): Promise<NearbyContract[]> {
    // console.log('[GeoLinkAPI] Fetching nearby contracts:', { latitude, longitude, radius });
    try {
      const response = await this.request<{ contracts?: NearbyContract[]; nearbyContracts?: NearbyContract[] }>(
        `/api/contracts/nearby?latitude=${latitude}&longitude=${longitude}&radius=${radius}`,
        {},
        false // Use data consumer key
      );
      const contracts = response.contracts || response.nearbyContracts || [];
      // console.log('[GeoLinkAPI] Fetched', contracts.length, 'nearby contracts');
      return contracts;
    } catch (error: any) {
      // If endpoint doesn't exist (404) or has missing parameters (400), return empty array
      if (
        error.message?.includes('404') || 
        error.message?.includes('not found') ||
        error.message?.includes('400') ||
        error.message?.includes('Missing required parameters') ||
        error.message?.includes('Bad Request')
      ) {
        // console.log('[GeoLinkAPI] Nearby contracts endpoint not available or requires different parameters');
        return [];
      }
      // console.warn('[GeoLinkAPI] Nearby contracts endpoint error:', error);
      return [];
    }
  }

  /**
   * Get wallet location by public key (uses Data Consumer key)
   */
  async getWalletLocation(publicKey: string): Promise<LocationUpdate | null> {
    try {
      return await this.request<LocationUpdate>(
        `/api/location/${publicKey}`,
        {},
        false // Use data consumer key
      );
    } catch (error) {
      // console.warn(`[GeoLinkAPI] Failed to get location for ${publicKey}:`, error);
      return null;
    }
  }

  /**
   * Get wallet location history (uses Wallet Provider key)
   */
  async getWalletLocationHistory(publicKey: string): Promise<any[]> {
    try {
      const response = await this.request<{ history?: any[] }>(
        `/api/locations/history?wallet_address=${publicKey}`,
        {},
        true // Use wallet provider key
      );
      return response.history || [];
    } catch (error) {
      // console.warn(`[GeoLinkAPI] Failed to get location history for ${publicKey}:`, error);
      return [];
    }
  }

  /**
   * Update privacy settings (uses Wallet Provider key)
   */
  async updatePrivacySettings(publicKey: string, privacyEnabled: boolean): Promise<any> {
    const privacyLevel = privacyEnabled ? 'public' : 'private';
    const requestBody = {
      public_key: publicKey,
      privacy_level: privacyLevel,
      location_sharing: privacyEnabled,
    };
    
    // console.log('[GeoLinkAPI] Updating privacy settings:', {
    //   public_key: publicKey.substring(0, 8) + '...',
    //   privacy_level: privacyLevel,
    // });
    
    return this.request('/api/wallet-provider/privacy-settings', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    }, true); // Use wallet provider key
  }

  /**
   * Update visibility settings (uses Wallet Provider key)
   */
  async updateVisibilitySettings(publicKey: string, isVisible: boolean): Promise<any> {
    const visibilityLevel = isVisible ? 'public' : 'private';
    const requestBody = {
      public_key: publicKey,
      visibility_level: visibilityLevel,
      show_location: isVisible,
    };
    
    // console.log('[GeoLinkAPI] Updating visibility settings:', {
    //   public_key: publicKey.substring(0, 8) + '...',
    //   visibility_level: visibilityLevel,
    // });
    
    return this.request('/api/wallet-provider/visibility-settings', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    }, true); // Use wallet provider key
  }

  /**
   * Create or update session
   * NOTE: GeoLink doesn't have a sessions endpoint - sessions are managed entirely on-chain in GeoTrust
   * This method is kept for API compatibility but does nothing
   */
  async createSession(sessionData: {
    session_id?: string;
    player1: string;
    player2?: string;
    state: 'waiting' | 'active' | 'ended';
    latitude?: number;
    longitude?: number;
  }): Promise<SessionData> {
    // GeoLink doesn't support sessions - return a mock response
    // console.log('[GeoLinkAPI] Sessions are managed on-chain, not in GeoLink');
    return {
      session_id: sessionData.session_id || '',
      player1: sessionData.player1,
      player2: sessionData.player2,
      state: sessionData.state,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  }

  /**
   * Get session by ID
   * NOTE: GeoLink doesn't have a sessions endpoint - sessions are managed entirely on-chain in GeoTrust
   * This method is kept for API compatibility but returns null
   */
  async getSession(sessionId: string): Promise<SessionData | null> {
    // console.log('[GeoLinkAPI] Sessions are managed on-chain, not in GeoLink');
    return null;
  }

  /**
   * Get active sessions for a user
   * NOTE: GeoLink doesn't have a sessions endpoint - sessions are managed entirely on-chain in GeoTrust
   * This method is kept for API compatibility but returns empty array
   */
  async getUserSessions(publicKey: string): Promise<SessionData[]> {
    // GeoLink doesn't support sessions - return empty array
    // Sessions are fetched from the on-chain contract instead
    return [];
  }

  /**
   * Create smart contract rule
   */
  async createContractRule(ruleData: {
    contract_address: string;
    function_name: string;
    conditions: any;
    execution_type: string;
    quorum?: {
      required_signatures: number;
      wallets: string[];
    };
  }): Promise<any> {
    return this.request('/api/contracts/rules', {
      method: 'POST',
      body: JSON.stringify(ruleData),
    });
  }

  /**
   * Get contract rules for a user.
   *
   * Sends `?public_key=<wallet>` per docs/geolink-multi-tenant-scoping-response.md
   * §3.1 (the legacy `?user=` query string was never honored by GeoLink and
   * fell through to the unscoped default — that's the bug that let Wallet B
   * see Wallet A's rules). Pairs with the `X-Public-Key` header set in
   * `request()` so non-admin sessions are scoped without ambiguity.
   */
  async getContractRules(publicKey: string): Promise<any[]> {
    try {
      const response = await this.request<{ rules?: any[] }>(
        `/api/contracts/rules?public_key=${encodeURIComponent(publicKey)}`
      );
      return response.rules || [];
    } catch (error) {
      // console.warn(`[GeoLinkAPI] Failed to get rules for ${publicKey}:`, error);
      return [];
    }
  }

  /** Update an existing execution rule (only the rule owner can do this) */
  async updateContractRule(ruleId: number, ruleData: Record<string, any>): Promise<any> {
    return this.request(`/api/contracts/rules/${ruleId}`, {
      method: 'PUT',
      body: JSON.stringify(ruleData),
    });
  }

  /** Delete an execution rule (only the rule owner can do this) */
  async deleteContractRule(ruleId: number): Promise<any> {
    return this.request(`/api/contracts/rules/${ruleId}`, { method: 'DELETE' });
  }

  /** Toggle active/inactive on an execution rule */
  async toggleContractRule(ruleId: number, is_active: boolean): Promise<any> {
    return this.request(`/api/contracts/rules/${ruleId}`, {
      method: 'PUT',
      body: JSON.stringify({ is_active }),
    });
  }

  /** Get registered contracts. When a wallet is connected we append
   *  `?public_key=` per docs/geolink-multi-tenant-scoping-response.md §3.1 so
   *  non-admin sessions only see rows authored by that wallet (admin JWTs
   *  ignore the filter and keep returning everything — see §3 of the guide).
   *  Uses the Data Consumer key. */
  async getContracts(): Promise<any[]> {
    try {
      const path = this.connectedPublicKey
        ? `/api/contracts?public_key=${encodeURIComponent(this.connectedPublicKey)}`
        : '/api/contracts';
      const response = await this.request<{ contracts?: any[] }>(path, {}, false);
      return response.contracts || [];
    } catch { return []; }
  }

  /**
   * Plumb the connected wallet so every subsequent request carries
   * `X-Public-Key: <wallet>` (and list calls scope themselves via
   * `?public_key=`). Pass `null` to clear the binding when the user logs
   * out — that returns the client to passive API-key-only behaviour.
   *
   * See docs/geolink-multi-tenant-scoping-response.md §5 Phase 1.
   */
  setConnectedPublicKey(publicKey: string | null): void {
    this.connectedPublicKey = publicKey || null;
  }

  /** Read-only accessor used in tests / debug surfaces. */
  getConnectedPublicKey(): string | null {
    return this.connectedPublicKey;
  }

  /**
   * Create multi-wallet quorum rule
   */
  async createQuorumRule(ruleData: {
    name: string;
    required_signatures: number;
    wallets: string[];
    contract_address?: string;
    function_name?: string;
    conditions?: any;
  }): Promise<any> {
    return this.request('/api/smart-wallet/quorum-rules', {
      method: 'POST',
      body: JSON.stringify(ruleData),
    });
  }

  /**
   * Get pending deposit actions for a user (uses Wallet Provider key)
   * GeoLink creates these when user location satisfies contract execution rules
   */
  /**
   * Get pending deposit actions for a user (uses Wallet Provider key)
   * GeoLink creates these when user location satisfies contract execution rules
   * 
   * According to API docs: GET /api/contracts/rules/pending/deposits
   * Query Parameters:
   * - public_key (optional): Filter by public key
   * - limit (optional): Limit results (default: 100)
   * 
   * Response: { success: true, pending_deposits: [...], count: N }
   */
  async getPendingDeposits(publicKey: string): Promise<{ pending_deposits: PendingDepositAction[] }> {
    try {
      const response = await this.request<{ 
        success?: boolean;
        pending_deposits?: PendingDepositAction[];
        deposits?: PendingDepositAction[];  // Backward compatibility
        count?: number;
      }>(
        `/api/contracts/rules/pending/deposits?public_key=${publicKey}`,
        {},
        true // Use wallet provider key
      );
      // Support both documented format and legacy format
      const deposits = response.pending_deposits || response.deposits || [];
      return { pending_deposits: deposits };
    } catch (error: any) {
      // If endpoint doesn't exist (404) or has missing parameters (400), return empty array
      if (
        error.message?.includes('404') || 
        error.message?.includes('not found') ||
        error.message?.includes('400') ||
        error.message?.includes('Missing required parameters') ||
        error.message?.includes('Bad Request')
      ) {
        // console.log('[GeoLinkAPI] Pending deposits endpoint not available or requires different parameters');
        return { pending_deposits: [] };
      }
      // console.warn('[GeoLinkAPI] Failed to get pending deposits:', error);
      return { pending_deposits: [] };
    }
  }

  /**
   * Get pending deposit action details (uses Wallet Provider key)
   */
  async getPendingDepositDetails(actionId: string): Promise<PendingDepositAction | null> {
    try {
      return await this.request<PendingDepositAction>(
        `/api/contracts/rules/pending/deposits/${actionId}`,
        {},
        true // Use wallet provider key
      );
    } catch (error) {
      // console.warn(`[GeoLinkAPI] Failed to get deposit details for ${actionId}:`, error);
      return null;
    }
  }

  /**
   * Execute a pending deposit action (uses Wallet Provider key)
   * Supports both WebAuthn (passkey) and wallet signing methods
   * 
   * According to API docs: POST /api/contracts/rules/pending/deposits/:action_id/execute
   * 
   * Request Body Options:
   * - Option 1: Signed XDR (RECOMMENDED - most secure)
   *   { signedXDR: "AAAAA..." }
   * 
   * - Option 2: WebAuthn data
   *   { passkeyPublicKeySPKI, webauthnSignature, webauthnAuthenticatorData, webauthnClientData, signaturePayload }
   * 
   * - Option 3: Server-side signing (less secure - backward compatibility)
   *   { user_public_key, user_secret_key }
   * 
   * Response: { success: true, result: { txHash, status, returnValue, ledger, stellarExpertUrl } }
   */
  async executePendingDeposit(actionId: string, executionData: {
    // Option 1: Signed XDR (RECOMMENDED)
    signedXDR?: string;
    
    // Option 2: WebAuthn (passkey)
    passkeyPublicKeySPKI?: string;
    webauthnSignature?: string;
    webauthnAuthenticatorData?: string;
    webauthnClientData?: string;
    signaturePayload?: string;
    
    // Option 3: Server-side signing (backward compatibility - less secure)
    user_public_key?: string;
    user_secret_key?: string;
    
    // Legacy field names (for backward compatibility)
    public_key?: string;
    webauthn_signature?: string;
    webauthn_authenticator_data?: string;
    webauthn_client_data?: string;
    passkey_public_key_spki?: string;
    transaction_xdr?: string;
  }): Promise<{
    success: boolean;
    result?: {
      txHash?: string;
      transaction_hash?: string;
      status?: string;
      returnValue?: any;
      ledger?: number;
      contractLogs?: any[];
      stellarExpertUrl?: string;
    };
    error?: string;
  }> {
    return this.request(
      `/api/contracts/rules/pending/deposits/${actionId}/execute`,
      {
        method: 'POST',
        body: JSON.stringify(executionData),
      },
      true // Use wallet provider key
    );
  }

  /**
   * Report deposit completion when executed directly via wallet signing
   * (Alternative to executePendingDeposit when using wallet signing)
   */
  async reportDepositCompletion(actionId: string, publicKey: string, transactionHash: string, ledger: number): Promise<any> {
    return this.request(
      `/api/contracts/rules/pending/deposits/${actionId}/complete`,
      {
        method: 'POST',
        body: JSON.stringify({
          public_key: publicKey,
          transaction_hash: transactionHash,
          ledger: ledger,
        }),
      },
      true // Use wallet provider key
    );
  }

  /**
   * Cancel a pending deposit action (uses Wallet Provider key)
   */
  async cancelPendingDeposit(actionId: string, publicKey: string): Promise<any> {
    return this.request(
      `/api/contracts/rules/pending/deposits/${actionId}/cancel`,
      {
        method: 'POST',
        body: JSON.stringify({ public_key: publicKey }),
      },
      true // Use wallet provider key
    );
  }
}

/**
 * Pending Deposit Action interface
 * Created by GeoLink when user location satisfies contract execution rules
 */
export interface PendingDepositAction {
  id: string;
  rule_id: number;
  rule_name: string;
  contract_id: number;
  contract_name: string;
  contract_address: string;
  function_name: string; // Usually "deposit"
  matched_public_key: string; // User's public key that matched
  update_id: number;
  received_at: string;
  parameters: {
    user_address?: string;
    asset?: string;
    amount?: string;
    [key: string]: any;
  };
  location?: {
    latitude: number;
    longitude: number;
  };
  expires_at: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
}

export const geolinkApi = new GeoLinkApiClient();

// ─────────────────────────────────────────────────────────────────────────────
// GeoLink Protocol Classification & Rule-Match API
// New endpoints for DeFindex contract classification and normalized rule matches
// ─────────────────────────────────────────────────────────────────────────────

/** Classification result returned by GeoLink for a Soroban contract address. */
export interface ContractClassification {
  contractAddress: string;
  network: 'testnet' | 'mainnet';
  protocol: 'defindex' | 'unknown';
  kind: 'factory' | 'vault' | 'strategy' | 'compatible-strategy' | 'unknown';
  confidence: 'high' | 'medium' | 'low';
  matchType: 'registry' | 'hash' | 'interface' | 'none';
  registryKey?: string | null;
  classifiedAt: string;
}

/** A single entry in the DeFindex registry returned by GeoLink. */
export interface DefindexRegistryEntry {
  address: string;
  kind: 'factory' | 'vault' | 'strategy' | 'compatible-strategy';
  registryKey: string;
  name?: string;
  description?: string;
  network: 'testnet' | 'mainnet';
}

/** Input payload sent to POST /api/rule-matches/normalize */
export interface NormalizedRuleMatchInput {
  ruleMatchId?: string;
  ruleId: string;
  walletPublicKey: string;
  contractAddress: string;
  contractFunction: string;
  network: 'testnet' | 'mainnet';
  countryCode?: number;
  asset?: string;
  amount?: string;
  sessionId?: string | null;
  matchedAt?: string;
  parameters?: Record<string, unknown>;
}

/** Normalized rule match returned by GeoLink — fully resolved and classified. */
export interface NormalizedRuleMatch {
  ruleMatchId: string;
  ruleId: string;
  walletPublicKey: string;
  sessionId?: string | null;
  countryCode: number;
  asset: string;
  amount: string;
  sourceContractAddress: string;
  sourceContractFunction: string;
  network: 'testnet' | 'mainnet';
  protocolClassification: ContractClassification;
  qualifiedAt: string;
  normalizedAt: string;
}

export class GeoLinkProtocolClient {
  private walletProviderKey: string;
  private dataConsumerKey: string;
  private baseUrl: string;

  /**
   * Keys default to empty: browser callers rely on the backend proxy to inject
   * the server-side credential. Only Node/test callers talking to GeoLink
   * directly pass keys here.
   */
  constructor(walletProviderKey?: string, dataConsumerKey?: string, baseUrl?: string) {
    this.walletProviderKey = walletProviderKey || '';
    this.dataConsumerKey = dataConsumerKey || '';
    this.baseUrl = baseUrl
      || (typeof window !== 'undefined' ? '/api/v2/geolink/proxy' : null)
      || process.env.REACT_APP_GEOLINK_API_URL
      || 'https://testnet.stellargeolink.com';
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    useWalletProviderKey = false,
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const apiKey = useWalletProviderKey ? this.walletProviderKey : this.dataConsumerKey;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (apiKey && apiKey.trim()) {
      headers['X-API-Key'] = apiKey.trim();
    }

    const response = await fetch(url, { ...options, headers });

    if (!response.ok) {
      let errorMessage = `GeoLink API ${response.status} ${response.statusText} — ${endpoint}`;
      try {
        const body: any = await response.json();
        errorMessage = body?.error || body?.message || errorMessage;
      } catch { /* ignore parse errors */ }

      const err = new Error(errorMessage);
      (err as any).status = response.status;
      throw err;
    }

    const json: unknown = await response.json();

    // Reject clearly malformed responses (null body from a 200 is suspicious)
    if (json === null || json === undefined) {
      throw new Error(`GeoLink returned empty body for ${endpoint}`);
    }

    return json as T;
  }

  /**
   * PART 1 — Method 1
   * Classify a Soroban contract by network and address.
   * GET /api/contracts/:network/:address/classification
   */
  async classifyContract(
    network: 'testnet' | 'mainnet',
    contractAddress: string,
  ): Promise<ContractClassification> {
    if (!contractAddress || contractAddress.length < 32) {
      throw new Error(`Invalid contract address: "${contractAddress}"`);
    }
    if (network !== 'testnet' && network !== 'mainnet') {
      throw new Error(`Invalid network: "${network}". Must be "testnet" or "mainnet".`);
    }

    const raw = await this.request<{ classification?: ContractClassification } & ContractClassification>(
      `/api/contracts/${network}/${contractAddress}/classification`,
      {},
      false,
    );

    // Accept both wrapped { classification: {...} } and flat response shapes
    const classification: ContractClassification = (raw as any).classification ?? raw;

    // Validate required fields — do not silently accept malformed responses
    const required: (keyof ContractClassification)[] = ['protocol', 'kind', 'confidence', 'matchType'];
    for (const field of required) {
      if (classification[field] === undefined || classification[field] === null) {
        throw new Error(`GeoLink classification response missing required field "${field}" for ${contractAddress}`);
      }
    }

    return {
      contractAddress: classification.contractAddress ?? contractAddress,
      network: classification.network ?? network,
      protocol: classification.protocol,
      kind: classification.kind,
      confidence: classification.confidence,
      matchType: classification.matchType,
      registryKey: classification.registryKey ?? null,
      classifiedAt: classification.classifiedAt ?? new Date().toISOString(),
    };
  }

  /**
   * PART 1 — Method 2
   * Fetch the DeFindex contract registry for a given network.
   * GET /api/protocols/defindex/registry/:network
   */
  async getDefindexRegistry(
    network: 'testnet' | 'mainnet',
  ): Promise<DefindexRegistryEntry[]> {
    const raw = await this.request<
      { entries?: DefindexRegistryEntry[]; registry?: DefindexRegistryEntry[] } | DefindexRegistryEntry[]
    >(
      `/api/protocols/defindex/registry/${network}`,
      {},
      false,
    );

    if (Array.isArray(raw)) return raw;

    const entries = (raw as any).entries ?? (raw as any).registry ?? [];

    if (!Array.isArray(entries)) {
      throw new Error(`GeoLink DeFindex registry response is not an array for network "${network}"`);
    }

    return entries;
  }

  /**
   * PART 1 — Method 3
   * Normalize an incoming rule match through GeoLink's classification pipeline.
   * POST /api/rule-matches/normalize
   */
  async normalizeRuleMatch(
    input: NormalizedRuleMatchInput,
  ): Promise<NormalizedRuleMatch> {
    if (!input.ruleId) throw new Error('normalizeRuleMatch: ruleId is required');
    if (!input.walletPublicKey) throw new Error('normalizeRuleMatch: walletPublicKey is required');
    if (!input.contractAddress) throw new Error('normalizeRuleMatch: contractAddress is required');
    if (!input.contractFunction) throw new Error('normalizeRuleMatch: contractFunction is required');

    const raw = await this.request<{ normalizedMatch?: NormalizedRuleMatch } & NormalizedRuleMatch>(
      '/api/rule-matches/normalize',
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
      true,
    );

    const match: NormalizedRuleMatch = (raw as any).normalizedMatch ?? raw;
    this.validateNormalizedRuleMatch(match);
    return match;
  }

  /**
   * PART 1 — Method 4
   * Fetch a previously normalized rule match by its ID.
   * GET /api/rule-matches/:id/normalized
   */
  async getNormalizedRuleMatch(ruleMatchId: string): Promise<NormalizedRuleMatch> {
    if (!ruleMatchId) throw new Error('getNormalizedRuleMatch: ruleMatchId is required');

    const raw = await this.request<{ normalizedMatch?: NormalizedRuleMatch } & NormalizedRuleMatch>(
      `/api/rule-matches/${encodeURIComponent(ruleMatchId)}/normalized`,
      {},
      true,
    );

    const match: NormalizedRuleMatch = (raw as any).normalizedMatch ?? raw;
    this.validateNormalizedRuleMatch(match);
    return match;
  }

  private validateNormalizedRuleMatch(match: NormalizedRuleMatch): void {
    const required: (keyof NormalizedRuleMatch)[] = [
      'ruleMatchId', 'ruleId', 'walletPublicKey', 'sourceContractAddress',
      'sourceContractFunction', 'network', 'protocolClassification',
      'qualifiedAt',
    ];
    for (const field of required) {
      if (match[field] === undefined || match[field] === null) {
        throw new Error(`GeoLink normalized rule match missing required field "${field}"`);
      }
    }
    if (typeof match.countryCode !== 'number') {
      throw new Error('GeoLink normalized rule match: countryCode must be a number');
    }
  }
}

/** Singleton protocol client — same base URL and keys as the main geolinkApi. */
export const geolinkProtocolClient = new GeoLinkProtocolClient();
